/**
 * P0/P1 Sprint — Labels wijzigen na aanmaak.
 *
 * Bewerk een bestaand shipping label zonder het trackingnummer of de
 * barcodewaarde te verliezen. Backend deep-merget op nested keys
 * (sender / recipient / shipment_details) zodat partial updates geen
 * andere velden wissen.
 *
 * Wat NIET wijzigbaar is via dit dialog:
 *  - tracking_number / barcodewaarde / label-ID
 *  - aanmaakdatum / oorspronkelijke counter
 *  - status (apart annulering-flow)
 *
 * Wat WEL wijzigbaar is:
 *  - sender (alle adres- en contactvelden)
 *  - recipient (alle adres- en contactvelden)
 *  - shipment_details (piece_count, gewicht, afmetingen, omschrijving,
 *    waarde, valuta, bestemming, interne notitie)
 *  - label_type, service_type, payment_status
 *  - handling_options
 *
 * Bij reeds-gedownload labels (`pdf_generated_at` gezet) tonen we een
 * waarschuwing: het oude PDF dat al verstuurd/geprint is bevat
 * verouderde data — gebruik altijd de nieuwe versie na een wijziging.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Save, X, AlertTriangle, Building2, Lock, Download,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";

import { adminApi, formatApiError } from "../../../context/AuthContext";
import NAWAddressBlock from "./NAWAddressBlock";

const SENDER_5999CARGO = Object.freeze({
  name: "5999Cargo Group Europe",
  company: "5999Cargo Group Europe",
  street: "Steenhouwerstraat",
  house_number: "88",
  house_number_addition: "",
  postal_code: "3194 AG",
  city: "Rotterdam",
  country: "Nederland",
  email: "info@5999cargo.nl",
  phone: "0488 23 5999",
  phone_alt: "0630 3521 60",
});

const EMPTY_PARTY = {
  name: "", company: "", phone: "", email: "",
  street: "", house_number: "", house_number_addition: "",
  postal_code: "", city: "", country: "",
};

function pickParty(p) {
  const src = p || {};
  return {
    name: src.name || "",
    company: src.company || "",
    phone: src.phone || "",
    email: src.email || "",
    street: src.street || "",
    house_number: src.house_number || "",
    house_number_addition: src.house_number_addition || "",
    postal_code: src.postal_code || "",
    city: src.city || "",
    country: src.country || "",
  };
}

function pickShipment(s) {
  const src = s || {};
  return {
    piece_count: src.piece_count ?? "",
    weight_kg: src.weight_kg ?? "",
    length_cm: src.length_cm ?? "",
    width_cm: src.width_cm ?? "",
    height_cm: src.height_cm ?? "",
    contents_description: src.contents_description || "",
    value: src.value ?? "",
    currency: src.currency || "EUR",
    destination: src.destination || "",
    internal_note: src.internal_note || "",
  };
}

export default function LabelEditDialog({
  label,
  meta,
  onClose,
  onSaved,
}) {
  const [sender, setSender] = useState(pickParty(label.sender));
  const [recipient, setRecipient] = useState(pickParty(label.recipient));
  const [shipment, setShipment] = useState(pickShipment(label.shipment_details));
  const [labelType, setLabelType] = useState(label.label_type || "package");
  const [serviceType, setServiceType] = useState(label.service_type || "seafreight");
  const [paymentStatus, setPaymentStatus] = useState(label.payment_status || "paid");
  const [handlingOptions, setHandlingOptions] = useState(label.handling_options || []);
  // Sprint jun 2026 (data continuity) — met colli is de zending-brede inhoud
  // een afgeleide waarde; dan mag dit veld niet als tweede waarheid dienen.
  const colliCount = ((label.shipment_details || {}).items || []).length;

  const [busy, setBusy] = useState(false);

  const wasDownloaded = !!label.pdf_generated_at;

  const handlingChoices = (meta?.handling_options || []).map((o) =>
    typeof o === "string" ? o : o.key
  );
  const handlingChoicesFinal = handlingChoices.length > 0
    ? handlingChoices
    : ["fragile", "stack_no", "keep_dry", "this_side_up", "perishable"];

  const labelTypes = (meta?.label_types || []).map((o) =>
    typeof o === "string" ? o : o.key
  );
  const labelTypesFinal = labelTypes.length > 0
    ? labelTypes
    : ["package", "pallet", "document"];

  const serviceTypes = (meta?.service_types || []).map((o) =>
    typeof o === "string" ? o : o.key
  );
  const serviceTypesFinal = serviceTypes.length > 0
    ? serviceTypes
    : ["seafreight", "airfreight", "road", "courier"];

  const paymentStatuses = (meta?.payment_statuses || []).map((o) =>
    typeof o === "string" ? o : o.key
  );
  const paymentStatusesFinal = paymentStatuses.length > 0
    ? paymentStatuses
    : ["paid", "not_paid", "payment_required", "released"];

  const setSenderField = (k, v) => setSender((p) => ({ ...p, [k]: v }));
  const setRecipientField = (k, v) => setRecipient((p) => ({ ...p, [k]: v }));
  const setShipmentField = (k, v) => setShipment((p) => ({ ...p, [k]: v }));

  const toggleHandling = (option) => {
    setHandlingOptions((cur) =>
      cur.includes(option) ? cur.filter((x) => x !== option) : [...cur, option],
    );
  };

  const applyCargoSender = () => {
    if (sender.name && sender.name !== SENDER_5999CARGO.name) {
      if (!window.confirm(
        "Huidige afzendergegevens overschrijven met 5999Cargo Group Europe?",
      )) return;
    }
    setSender({ ...EMPTY_PARTY, ...SENDER_5999CARGO });
    toast.success("5999Cargo afzendergegevens ingevuld");
  };

  const buildPayload = () => {
    const payload = {
      label_type: labelType,
      service_type: serviceType,
      payment_status: paymentStatus,
      handling_options: handlingOptions,
      sender,
      recipient,
      shipment_details: {
        ...shipment,
        // numeric coercion
        piece_count: shipment.piece_count === "" ? null : Number(shipment.piece_count),
        weight_kg: shipment.weight_kg === "" ? null : Number(shipment.weight_kg),
        length_cm: shipment.length_cm === "" ? null : Number(shipment.length_cm),
        width_cm: shipment.width_cm === "" ? null : Number(shipment.width_cm),
        height_cm: shipment.height_cm === "" ? null : Number(shipment.height_cm),
        value: shipment.value === "" ? null : Number(shipment.value),
      },
    };
    return payload;
  };

  const handleSave = async ({ openPdfAfter = false } = {}) => {
    // Validate minimum
    if (!recipient.name?.trim()) {
      toast.error("Naam ontvanger is verplicht");
      return null;
    }
    if (!shipment.piece_count || Number(shipment.piece_count) < 1) {
      toast.error("Aantal colli moet minimaal 1 zijn");
      return null;
    }
    setBusy(true);
    try {
      const r = await adminApi.patch(
        `/admin/shipping-labels/${label.id}`,
        buildPayload(),
      );
      toast.success("Label bijgewerkt");
      onSaved && onSaved(r.data);
      if (openPdfAfter) {
        // Open fresh PDF in a new tab with cache-busting
        try {
          const pdfResp = await adminApi.get(
            `/admin/shipping-labels/${label.id}/pdf?_ts=${Date.now()}`,
            { responseType: "blob" },
          );
          const blob = new Blob([pdfResp.data], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const win = window.open(url, "_blank");
          // revoke after window had time to load
          setTimeout(() => {
            try { URL.revokeObjectURL(url); } catch (e) { void e; }
          }, 60_000);
          if (!win) {
            toast.error("Pop-up geblokkeerd — sta pop-ups toe om de PDF te openen.");
          }
        } catch (pdfErr) {
          toast.error(formatApiError(pdfErr) || "PDF openen mislukt");
        }
      }
      onClose();
      return r.data;
    } catch (err) {
      toast.error(formatApiError(err) || "Opslaan mislukt");
      return null;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="label-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50]">
            Label bewerken · {label.tracking_number}
          </DialogTitle>
          <DialogDescription>
            Pas de gegevens aan en sla op. <strong>Trackingnummer en barcode blijven ongewijzigd.</strong>
          </DialogDescription>
        </DialogHeader>

        {/* Read-only banner */}
        <div
          className="bg-amber-50 border border-amber-200 rounded-md p-3 my-2 flex items-start gap-2"
          data-testid="label-edit-readonly-banner"
        >
          <Lock className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            <div className="font-bold">
              Trackingnummer & barcode: <span className="font-mono">{label.tracking_number}</span> (vergrendeld)
            </div>
            <p className="mt-0.5">
              Deze blijven ongewijzigd. Het label-ID, de aanmaakdatum en de counter zijn ook read-only.
            </p>
          </div>
        </div>

        {wasDownloaded && (
          <div
            className="bg-red-50 border border-red-200 rounded-md p-3 my-2 flex items-start gap-2"
            data-testid="label-edit-printed-warning"
          >
            <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-900">
              <div className="font-bold">Let op: dit label is al eerder gedownload/geprint.</div>
              <p className="mt-0.5">
                Download en gebruik na wijziging <strong>altijd</strong> de nieuwe versie. De oude geprinte versie bevat verouderde gegevens.
              </p>
            </div>
          </div>
        )}

        {/* Zending-velden */}
        <div className="rounded-md border border-gray-200 p-3 my-2 space-y-2">
          <h4 className="font-chivo font-bold text-xs text-[#082c50] uppercase tracking-wide">
            Zending
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Label type</Label>
              <Select value={labelType} onValueChange={setLabelType}>
                <SelectTrigger className="rounded-md text-xs" data-testid="label-edit-label-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {labelTypesFinal.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Service</Label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger className="rounded-md text-xs" data-testid="label-edit-service-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypesFinal.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Betaalstatus</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger className="rounded-md text-xs" data-testid="label-edit-payment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentStatusesFinal.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt.toUpperCase().replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Aantal colli *</Label>
              <Input
                type="number" min={1} max={999}
                value={shipment.piece_count}
                onChange={(e) => setShipmentField("piece_count", e.target.value)}
                className="rounded-md text-xs"
                data-testid="label-edit-piece-count"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Gewicht (kg)</Label>
              <Input
                type="number" step="0.01"
                value={shipment.weight_kg}
                onChange={(e) => setShipmentField("weight_kg", e.target.value)}
                className="rounded-md text-xs"
                data-testid="label-edit-weight"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Bestemming</Label>
              <Input
                value={shipment.destination}
                onChange={(e) => setShipmentField("destination", e.target.value)}
                className="rounded-md text-xs"
                data-testid="label-edit-destination"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">Waarde</Label>
              <div className="flex gap-1">
                <Input
                  type="number" step="0.01"
                  value={shipment.value}
                  onChange={(e) => setShipmentField("value", e.target.value)}
                  className="rounded-md text-xs flex-1"
                  data-testid="label-edit-value"
                />
                <Input
                  value={shipment.currency}
                  onChange={(e) => setShipmentField("currency", e.target.value.toUpperCase())}
                  maxLength={3}
                  className="rounded-md text-xs w-14"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">L (cm)</Label>
              <Input
                type="number" value={shipment.length_cm}
                onChange={(e) => setShipmentField("length_cm", e.target.value)}
                className="rounded-md text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">B (cm)</Label>
              <Input
                type="number" value={shipment.width_cm}
                onChange={(e) => setShipmentField("width_cm", e.target.value)}
                className="rounded-md text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-gray-600">H (cm)</Label>
              <Input
                type="number" value={shipment.height_cm}
                onChange={(e) => setShipmentField("height_cm", e.target.value)}
                className="rounded-md text-xs"
              />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">Omschrijving inhoud</Label>
            {colliCount > 0 ? (
              <div className="border border-gray-200 bg-gray-50 rounded-md p-2"
                   data-testid="label-edit-contents-derived">
                <p className="text-xs text-gray-700 break-words">
                  {shipment.contents_description || "—"}
                </p>
                <p className="text-[10px] text-gray-500 mt-1">
                  Inhoud wordt automatisch opgebouwd uit de {colliCount} colli van
                  deze zending. Pas de inhoud aan via de Colli-tab van de zending.
                </p>
              </div>
            ) : (
              <Textarea
                value={shipment.contents_description}
                onChange={(e) => setShipmentField("contents_description", e.target.value)}
                rows={2} maxLength={500}
                className="rounded-md text-xs"
                data-testid="label-edit-contents"
              />
            )}
          </div>

          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">Interne notitie</Label>
            <Textarea
              value={shipment.internal_note}
              onChange={(e) => setShipmentField("internal_note", e.target.value)}
              rows={2} maxLength={500}
              className="rounded-md text-xs bg-amber-50/50 border-amber-200"
              data-testid="label-edit-internal-note"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase font-bold text-gray-600">Handling opties</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {handlingChoicesFinal.map((opt) => (
                <label key={opt} className="inline-flex items-center gap-1 text-xs cursor-pointer bg-gray-50 border border-gray-200 rounded px-2 py-1">
                  <Checkbox
                    checked={handlingOptions.includes(opt)}
                    onCheckedChange={() => toggleHandling(opt)}
                    data-testid={`label-edit-handling-${opt}`}
                  />
                  <span className="uppercase font-bold">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Recipient */}
        <PartyForm
          title="Ontvanger"
          party={recipient}
          onChange={setRecipientField}
          testIdPrefix="label-edit-recipient"
          showNameAsterisk
        />

        {/* Sender */}
        <div className="rounded-md border border-gray-200 p-3 my-2 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h4 className="font-chivo font-bold text-xs text-[#082c50] uppercase tracking-wide">
              Afzender
            </h4>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={applyCargoSender}
              className="rounded-md text-xs border-[#082c50]/30 text-[#082c50] hover:bg-[#082c50]/5"
              data-testid="label-edit-use-cargo-sender"
            >
              <Building2 className="w-3 h-3 mr-1" />
              Gebruik 5999Cargo als afzender
            </Button>
          </div>
          <PartyFormFields
            party={sender}
            onChange={setSenderField}
            testIdPrefix="label-edit-sender"
          />
        </div>

        <DialogFooter className="gap-2 pt-3 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl text-xs"
            data-testid="label-edit-cancel"
          >
            <X className="w-3 h-3 mr-1" /> Annuleren
          </Button>
          <Button
            onClick={() => handleSave({ openPdfAfter: false })}
            disabled={busy}
            className="bg-[#082c50] text-white rounded-xl text-xs"
            data-testid="label-edit-save"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Opslaan
          </Button>
          <Button
            onClick={() => handleSave({ openPdfAfter: true })}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs"
            data-testid="label-edit-save-and-pdf"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
            Opslaan & PDF openen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PartyForm({ title, party, onChange, testIdPrefix, showNameAsterisk }) {
  return (
    <div className="rounded-md border border-gray-200 p-3 my-2 space-y-2">
      <h4 className="font-chivo font-bold text-xs text-[#082c50] uppercase tracking-wide">{title}</h4>
      <PartyFormFields
        party={party}
        onChange={onChange}
        testIdPrefix={testIdPrefix}
        nameRequired={showNameAsterisk}
      />
    </div>
  );
}

function PartyFormFields({ party, onChange, testIdPrefix, nameRequired }) {
  // P1 — Adresblok komt via NAWAddressBlock met PDOK-postcode lookup. Naam,
  // bedrijf, telefoon en e-mail blijven losse velden (geen onderdeel NAW).
  const onAddressPatch = (patch) => {
    for (const k of ["street", "house_number", "house_number_addition", "postal_code", "city", "country"]) {
      if (Object.prototype.hasOwnProperty.call(patch, k)) onChange(k, patch[k]);
    }
  };
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase font-bold text-gray-600">
            Naam{nameRequired ? " *" : ""}
          </Label>
          <Input
            value={party.name}
            onChange={(e) => onChange("name", e.target.value)}
            className="rounded-md text-xs"
            data-testid={`${testIdPrefix}-name`}
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-bold text-gray-600">Bedrijf</Label>
          <Input
            value={party.company}
            onChange={(e) => onChange("company", e.target.value)}
            className="rounded-md text-xs"
            data-testid={`${testIdPrefix}-company`}
          />
        </div>
      </div>
      <NAWAddressBlock
        value={{
          street: party.street,
          house_number: party.house_number,
          house_number_addition: party.house_number_addition,
          postal_code: party.postal_code,
          city: party.city,
          country: party.country,
        }}
        onChange={onAddressPatch}
        testidPrefix={testIdPrefix}
        title="Adres (postcode + huisnummer voor PDOK-lookup)"
        hint="Vul postcode + huisnummer in (NL) voor automatisch ophalen van straat en plaats."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase font-bold text-gray-600">Telefoon</Label>
          <Input
            value={party.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            className="rounded-md text-xs"
            data-testid={`${testIdPrefix}-phone`}
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-bold text-gray-600">E-mail</Label>
          <Input
            type="email"
            value={party.email}
            onChange={(e) => onChange("email", e.target.value)}
            className="rounded-md text-xs"
            data-testid={`${testIdPrefix}-email`}
          />
        </div>
      </div>
    </>
  );
}
